/**
 * NotificationPreferences Component (T172 - US11)
 * Notification preferences management
 * UI Design: Extracted from prototypes/figma-bquest-app/src/components/ProviderPortal.tsx settings tab
 */

import { useEffect, useState } from 'react';
import { Label } from '@/shared/components/ui/label';
import { Checkbox } from '@/shared/components/ui/checkbox';

type NotificationPreferencesProps = {
  userRole: string;
  initialPreferences?: {
    emailMessages?: boolean;
    emailLeads?: boolean;
  };
  onChange?: (preferences: NotificationPreferencesData) => void;
};

export type NotificationPreferencesData = {
  emailMessages: boolean;
  emailLeads: boolean;
};

export const NotificationPreferences = ({ 
  userRole, 
  initialPreferences = {},
  onChange 
}: NotificationPreferencesProps) => {
  const [emailMessages, setEmailMessages] = useState(initialPreferences.emailMessages ?? true);
  const [emailLeads, setEmailLeads] = useState(initialPreferences.emailLeads ?? true);

  // Only show "email about leads" for service providers
  const isServiceProvider = userRole === 'SERVICE_PROVIDER';

  useEffect(() => {
    setEmailMessages(initialPreferences.emailMessages ?? true);
    setEmailLeads(initialPreferences.emailLeads ?? true);
  }, [initialPreferences.emailMessages, initialPreferences.emailLeads]);

  const handleEmailMessagesChange = (checked: boolean | 'indeterminate') => {
    const nextEmailMessages = checked === true;
    setEmailMessages(nextEmailMessages);
    onChange?.({
      emailMessages: nextEmailMessages,
      emailLeads,
    });
  };

  const handleEmailLeadsChange = (checked: boolean | 'indeterminate') => {
    const nextEmailLeads = checked === true;
    setEmailLeads(nextEmailLeads);
    onChange?.({
      emailMessages,
      emailLeads: nextEmailLeads,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-base">Notification Preferences</Label>
        <p className="text-sm text-gray-600 mt-1">
          Choose how you want to be notified
        </p>
      </div>

      <div className="mt-3 space-y-3">
        <div className="flex items-center gap-2">
          <Checkbox 
            id="email-messages" 
            checked={emailMessages}
            onCheckedChange={handleEmailMessagesChange}
          />
          <div>
            <label
              htmlFor="email-messages"
              className="text-sm text-gray-700 cursor-pointer"
            >
              Email me about new messages
            </label>
            <p className="text-xs text-gray-500">
              Receive email notifications for unread messages
            </p>
          </div>
        </div>

        {isServiceProvider && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="email-leads"
              checked={emailLeads}
              onCheckedChange={handleEmailLeadsChange}
            />
            <label
              htmlFor="email-leads"
              className="text-sm text-gray-700 cursor-pointer"
            >
              Email me about new consultation requests
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

